import { jitterAngle } from "./physics/config";
import { clamp } from "../util/math";
import { effectiveCells, shipCellSize } from "./ship";
import type { Crew, EntityId, Ship, ShipModule, World } from "./types";

const TRANSIT_S_PER_CELL = 0.4;

const FIRST_NAMES = [
  "Vera", "Kade", "Iola", "Renn", "Suri", "Tobe", "Mira", "Hux",
  "Pell", "Anya", "Jory", "Kit", "Lior", "Nim", "Osha", "Quill",
  "Rho", "Sela", "Tev", "Una", "Wren", "Yash", "Zev", "Bren",
];
const LAST_NAMES = [
  "Voss", "Holm", "Marek", "Okun", "Rys", "Sable", "Tann", "Velar",
  "Aro", "Crane", "Dax", "Eder", "Frey", "Glas", "Hest", "Iko",
];

let _nameCursor = 0;
function nextName(): string {
  const i = _nameCursor++;
  const f = FIRST_NAMES[i % FIRST_NAMES.length];
  const l = LAST_NAMES[Math.floor(i / FIRST_NAMES.length) % LAST_NAMES.length];
  return `${f} ${l}`;
}

function forEachCell(m: ShipModule, fn: (gx: number, gy: number) => void): void {
  const eff = effectiveCells(m.def, m.facing);
  for (let dy = 0; dy < eff.h; dy++) {
    for (let dx = 0; dx < eff.w; dx++) fn(m.gx + dx, m.gy + dy);
  }
}

function quartersCells(ship: Ship): { gx: number; gy: number }[] {
  const out: { gx: number; gy: number }[] = [];
  for (const m of ship.modules) {
    if (m.hp <= 0 || m.def.stats.crewCapacity <= 0) continue;
    forEachCell(m, (gx, gy) => out.push({ gx, gy }));
  }
  return out;
}

function walkableCellSet(ship: Ship): Set<string> {
  const s = new Set<string>();
  for (const m of ship.modules) {
    if (m.hp <= 0 || !m.def.stats.crewTraversable) continue;
    forEachCell(m, (gx, gy) => s.add(`${gx},${gy}`));
  }
  return s;
}

export function populateCrew(
  world: World,
  ship: Ship,
  count: number,
  friendliness: number,
): Crew[] {
  const cells = quartersCells(ship);
  if (cells.length === 0 || count <= 0) return [];
  const spawned: Crew[] = [];
  for (let i = 0; i < count; i++) {
    const cell = cells[i % cells.length];
    const id: EntityId = world.nextId++;
    const crew: Crew = {
      id,
      shipId: ship.id,
      name: nextName(),
      cellGx: cell.gx,
      cellGy: cell.gy,
      cellSlot: 0,
      assignedModule: null,
      path: [],
      stepStartTime: 0,
      cellStepS: TRANSIT_S_PER_CELL,
      friendliness,
      health: 100,
    };
    world.crew.set(id, crew);
    crew.cellSlot = pickFreeCellSlot(world, crew);
    spawned.push(crew);
  }
  return spawned;
}

function pickFreeCellSlot(world: World, crew: Crew): number {
  const taken = new Set<number>();
  for (const c of world.crew.values()) {
    if (c.id === crew.id) continue;
    if (c.path.length > 0) continue;
    if (c.shipId !== crew.shipId) continue;
    if (c.cellGx !== crew.cellGx || c.cellGy !== crew.cellGy) continue;
    taken.add(c.cellSlot);
  }
  for (let i = 0; i < 4; i++) if (!taken.has(i)) return i;
  return crew.id % 4;
}

function bfsPathToModule(
  ship: Ship,
  fgx: number,
  fgy: number,
  target: ShipModule,
): { gx: number; gy: number }[] | null {
  const walk = walkableCellSet(ship);
  const targetCells = new Set<string>();
  forEachCell(target, (gx, gy) => targetCells.add(`${gx},${gy}`));
  const startKey = `${fgx},${fgy}`;
  if (!walk.has(startKey) && !targetCells.has(startKey)) return null;
  if (targetCells.has(startKey)) return [];

  const parent = new Map<string, string>();
  parent.set(startKey, "");
  let head: { gx: number; gy: number }[] = [{ gx: fgx, gy: fgy }];
  while (head.length > 0) {
    const next: typeof head = [];
    for (const cur of head) {
      const curKey = `${cur.gx},${cur.gy}`;
      for (const n of [
        { gx: cur.gx + 1, gy: cur.gy },
        { gx: cur.gx - 1, gy: cur.gy },
        { gx: cur.gx, gy: cur.gy + 1 },
        { gx: cur.gx, gy: cur.gy - 1 },
      ]) {
        const k = `${n.gx},${n.gy}`;
        if (parent.has(k)) continue;
        if (targetCells.has(k) && walk.has(k)) {
          parent.set(k, curKey);
          return reconstructPath(parent, startKey, k);
        }
        if (!walk.has(k)) continue;
        parent.set(k, curKey);
        next.push(n);
      }
    }
    head = next;
  }
  return null;
}

function reconstructPath(
  parent: Map<string, string>,
  startKey: string,
  endKey: string,
): { gx: number; gy: number }[] {
  const out: { gx: number; gy: number }[] = [];
  let cur = endKey;
  while (cur !== startKey) {
    const [sx, sy] = cur.split(",");
    out.push({ gx: Number(sx), gy: Number(sy) });
    cur = parent.get(cur) ?? startKey;
  }
  out.reverse();
  return out;
}

function findLockedDockPair(
  from: Ship,
  to: Ship,
  originGx: number,
  originGy: number,
): { sourceCollar: ShipModule; peerCollar: ShipModule } | null {
  let best: { sourceCollar: ShipModule; peerCollar: ShipModule; d2: number } | null = null;
  for (const m of from.modules) {
    if (m.hp <= 0 || !m.docking) continue;
    if (m.docking.peerShipId !== to.id) continue;
    const idx = m.docking.peerModuleIdx;
    if (idx == null) continue;
    const peer = to.modules[idx];
    if (!peer || peer.hp <= 0 || !peer.docking) continue;
    const dx = m.gx - originGx, dy = m.gy - originGy;
    const d2 = dx * dx + dy * dy;
    if (!best || d2 < best.d2) best = { sourceCollar: m, peerCollar: peer, d2 };
  }
  return best ? { sourceCollar: best.sourceCollar, peerCollar: best.peerCollar } : null;
}

/** hostShip may differ from crew's current ship via a locked dock.
 *  null moduleIdx clears the assignment. */
export function assignCrewToModule(
  world: World,
  crew: Crew,
  hostShip: Ship,
  moduleIdx: number | null,
): boolean {
  if (moduleIdx == null) {
    crew.assignedModule = null;
    crew.path = [];
    return true;
  }
  const target = hostShip.modules[moduleIdx];
  if (!target || target.hp <= 0) return false;
  const fromShip = world.ships.get(crew.shipId);
  if (!fromShip) return false;

  const path = buildPathToModule(fromShip, crew.cellGx, crew.cellGy, hostShip, target);
  if (!path) return false;
  crew.assignedModule = target;
  crew.path = path;
  crew.stepStartTime = world.time;
  crew.cellStepS = TRANSIT_S_PER_CELL;
  return true;
}

function buildPathToModule(
  fromShip: Ship,
  fromGx: number,
  fromGy: number,
  hostShip: Ship,
  target: ShipModule,
): { shipId: EntityId; gx: number; gy: number }[] | null {
  if (fromShip.id === hostShip.id) {
    const cells = bfsPathToModule(fromShip, fromGx, fromGy, target);
    if (!cells) return null;
    return cells.map((c) => ({ shipId: fromShip.id, gx: c.gx, gy: c.gy }));
  }
  const pair = findLockedDockPair(fromShip, hostShip, fromGx, fromGy);
  if (!pair) return null;
  const leg1 = bfsPathToModule(fromShip, fromGx, fromGy, pair.sourceCollar);
  if (!leg1) return null;
  const leg3 = bfsPathToModule(hostShip, pair.peerCollar.gx, pair.peerCollar.gy, target);
  if (!leg3) return null;
  const out: { shipId: EntityId; gx: number; gy: number }[] = [];
  for (const c of leg1) out.push({ shipId: fromShip.id, gx: c.gx, gy: c.gy });
  out.push({ shipId: hostShip.id, gx: pair.peerCollar.gx, gy: pair.peerCollar.gy });
  for (const c of leg3) out.push({ shipId: hostShip.id, gx: c.gx, gy: c.gy });
  return out;
}

const _hostOf = new Map<ShipModule, Ship>();

export function stepCrew(world: World): void {
  // Module→ship index, built once per tick, replaces the per-crew
  // O(ships × modules) `s.modules.includes(m)` scans below. A module belongs to
  // exactly one ship, so has(m) ⟺ some ship includes m (dead modules included,
  // matching the array membership the scans tested).
  _hostOf.clear();
  for (const s of world.ships.values()) {
    for (const m of s.modules) _hostOf.set(m, s);
  }

  for (const crew of world.crew.values()) {
    const ship = world.ships.get(crew.shipId);
    if (!ship) {
      world.crew.delete(crew.id);
      continue;
    }
    if (!cellIsAlive(ship, crew.cellGx, crew.cellGy)) {
      world.crew.delete(crew.id);
      continue;
    }
    if (crew.assignedModule != null) {
      const m = crew.assignedModule;
      const stillOk = m.hp > 0 && _hostOf.has(m);
      if (!stillOk) {
        crew.assignedModule = null;
        crew.path = [];
      }
    }
    const wasWalking = crew.path.length > 0;
    while (crew.path.length > 0
        && world.time >= crew.stepStartTime + crew.cellStepS) {
      const next = crew.path[0];
      const nextShip = world.ships.get(next.shipId);
      if (!nextShip) {
        crew.path = [];
        crew.assignedModule = null;
        break;
      }
      if (!cellIsAlive(nextShip, next.gx, next.gy)) {
        if (crew.assignedModule) {
          const hostShip = _hostOf.get(crew.assignedModule) ?? null;
          if (hostShip) {
            const repath = buildPathToModule(ship, crew.cellGx, crew.cellGy, hostShip, crew.assignedModule);
            if (repath && repath.length > 0) {
              crew.path = repath;
              crew.stepStartTime = world.time;
              break;
            }
          }
        }
        crew.path = [];
        crew.assignedModule = null;
        break;
      }
      crew.shipId = next.shipId;
      crew.cellGx = next.gx;
      crew.cellGy = next.gy;
      crew.path.shift();
      crew.stepStartTime += crew.cellStepS;
    }
    if (wasWalking && crew.path.length === 0) {
      crew.cellSlot = pickFreeCellSlot(world, crew);
    }
  }
}

function cellIsAlive(ship: Ship, gx: number, gy: number): boolean {
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    if (gx >= m.gx && gx < m.gx + eff.w
        && gy >= m.gy && gy < m.gy + eff.h) return true;
  }
  return false;
}

export function crewOnShip(world: World, ship: Ship): Crew[] {
  const out: Crew[] = [];
  for (const c of world.crew.values()) if (c.shipId === ship.id) out.push(c);
  return out;
}

export function crewMannedCount(
  world: World,
  ship: Ship,
  moduleIdx: number,
): number {
  const target = ship.modules[moduleIdx];
  if (!target) return 0;
  let n = 0;
  for (const c of world.crew.values()) {
    if (c.shipId !== ship.id) continue;
    if (c.assignedModule !== target) continue;
    if (c.path.length > 0) continue;
    n++;
  }
  return n;
}

export function crewInComponentOf(
  world: World,
  ship: Ship,
  rootGx: number,
  rootGy: number,
): Crew[] {
  const walk = walkableCellSet(ship);
  const startKey = `${rootGx},${rootGy}`;
  const reachable = new Set<string>();
  if (walk.has(startKey)) {
    reachable.add(startKey);
    const stack: { gx: number; gy: number }[] = [{ gx: rootGx, gy: rootGy }];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const n of [
        { gx: cur.gx + 1, gy: cur.gy },
        { gx: cur.gx - 1, gy: cur.gy },
        { gx: cur.gx, gy: cur.gy + 1 },
        { gx: cur.gx, gy: cur.gy - 1 },
      ]) {
        const k = `${n.gx},${n.gy}`;
        if (reachable.has(k) || !walk.has(k)) continue;
        reachable.add(k);
        stack.push(n);
      }
    }
  }
  const out: Crew[] = [];
  for (const c of world.crew.values()) {
    if (c.shipId !== ship.id) continue;
    if (!reachable.has(`${c.cellGx},${c.cellGy}`)) continue;
    out.push(c);
  }
  return out;
}

export function crewWorldPos(
  world: World,
  crew: Crew,
  time: number,
  slotOffset?: { dx: number; dy: number },
): { x: number; y: number } {
  const ship = world.ships.get(crew.shipId);
  if (!ship) return { x: 0, y: 0 };
  const here = cellWorld(ship, crew.cellGx, crew.cellGy, slotOffset);
  if (crew.path.length === 0 || crew.cellStepS <= 0) return here;
  const next = crew.path[0];
  const nextShip = world.ships.get(next.shipId);
  if (!nextShip) return here;
  const there = cellWorld(nextShip, next.gx, next.gy);
  const t = clamp((time - crew.stepStartTime) / crew.cellStepS, 0, 1);
  return { x: here.x + (there.x - here.x) * t, y: here.y + (there.y - here.y) * t };
}

/** 2×2 slots at ±cellSize/4 (0=TL, 1=TR, 2=BL, 3=BR). */
export function crewCellSlotOffset(
  slot: number,
  cellSize: number,
): { dx: number; dy: number } {
  const off = cellSize * 0.25;
  const s = ((slot % 4) + 4) % 4;
  const col = s % 2;
  const row = (s / 2) | 0;
  return { dx: col === 0 ? -off : off, dy: row === 0 ? -off : off };
}

function cellWorld(
  ship: Ship,
  gx: number,
  gy: number,
  offset?: { dx: number; dy: number },
): { x: number; y: number } {
  const cs = shipCellSize(ship);
  const ox = offset?.dx ?? 0;
  const oy = offset?.dy ?? 0;
  const localX = (gx + 0.5) * cs + ox - ship.com.x;
  const localY = (gy + 0.5) * cs + oy - ship.com.y;
  const rot = jitterAngle(ship.body.rot);
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  return {
    x: ship.body.pos.x + localX * c - localY * s,
    y: ship.body.pos.y + localX * s + localY * c,
  };
}

export function reassignCrewAfterSplit(
  world: World,
  origin: Ship,
  siblings: Ship[],
): void {
  if (siblings.length === 0) return;
  for (const crew of world.crew.values()) {
    if (crew.shipId !== origin.id) continue;
    if (cellIsAlive(origin, crew.cellGx, crew.cellGy)) continue;
    for (const s of siblings) {
      if (cellIsAlive(s, crew.cellGx, crew.cellGy)) {
        crew.shipId = s.id;
        if (!crew.assignedModule || !s.modules.includes(crew.assignedModule)) {
          crew.assignedModule = null;
        }
        crew.path = [];
        break;
      }
    }
  }
}
